import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, DollarSign, Truck, Percent, Sparkles, Zap, Star, Receipt, Banknote, Building2, Landmark, Clock } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const FIELDS = [
  {
    key: "subscription_fee_amount",
    unit: "$",
    Icon: Receipt,
    color: "#6B2C8F",
    titleKey: "settings.subscriptionFee",
    helpKey: "settings.subscriptionFeeHelp",
  },
  {
    key: "service_fee_normal_percent",
    unit: "%",
    Icon: DollarSign,
    color: "#D81B8C",
    titleKey: "settings.serviceFeeNormal",
    helpKey: "settings.serviceFeeNormalHelp",
  },
  {
    key: "service_fee_lastminute_percent",
    unit: "%",
    Icon: Zap,
    color: "#D81B8C",
    titleKey: "settings.serviceFeeLastMinute",
    helpKey: "settings.serviceFeeLastMinuteHelp",
  },
  {
    key: "commission_special_percent",
    unit: "%",
    Icon: Star,
    color: "#6B2C8F",
    titleKey: "settings.commissionSpecial",
    helpKey: "settings.commissionSpecialHelp",
  },
  {
    key: "home_delivery_fee",
    unit: "$",
    Icon: Truck,
    color: "#3FA535",
    titleKey: "settings.homeDeliveryFee",
    helpKey: "settings.homeDeliveryFeeHelp",
  },
  {
    key: "tva_percent",
    unit: "%",
    Icon: Percent,
    color: "#5C4664",
    titleKey: "settings.tva",
    helpKey: "settings.tvaHelp",
  },
];

export default function AdminSettings() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => setForm(r.data))
      .catch(() => setError("Impossible de charger les paramètres."))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => {
        const n = parseFloat(form[key]);
        if (!Number.isNaN(n)) payload[key] = n;
      });
      payload.currency = form.currency;
      payload.subscription_fee_enabled = !!form.subscription_fee_enabled;
      // Cash payment configuration
      const windowH = parseInt(form.cash_payment_window_hours, 10);
      if (!Number.isNaN(windowH)) payload.cash_payment_window_hours = windowH;
      [
        "cash_office_address",
        "cash_office_hours",
        "cash_bank_name",
        "cash_bank_account",
        "cash_bank_beneficiary",
      ].forEach((k) => {
        if (typeof form[k] === "string") payload[k] = form[k];
      });
      const { data } = await api.patch("/admin/settings", payload);
      setForm(data);
      toast.success(t("settings.saved"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="max-w-3xl mx-auto px-5 pt-12 text-center text-[#5C4664]">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  // Live preview: $100 supplier-cost → final price for each gift type
  const samplePreview = (rate) => {
    const supplier = 100;
    const fee = (supplier * rate) / 100;
    const tva = (fee * form.tva_percent) / 100;
    return (supplier + fee + tva).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24" data-testid="admin-settings-page">
      <TopBar />
      <div className="max-w-3xl mx-auto px-5 pt-6">
        <button
          onClick={() => navigate("/admin")}
          data-testid="settings-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>

        <div className="mt-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-2">
            {t("admin.dashboard")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
            {t("settings.pageTitle")}
          </h1>
          <p className="mt-2 text-sm text-[#5C4664] max-w-2xl">
            {t("settings.pageSub")}
          </p>
        </div>

        <form onSubmit={save} className="mt-8 space-y-4">
          {/* Currency selector */}
          <div className="bg-white border border-[#F2E2EC] rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-black text-[#2A0E3C]">
                  {t("settings.currency")}
                </h3>
                <p className="text-xs text-[#5C4664]">{t("settings.currencyHelp")}</p>
              </div>
            </div>
            <Select
              value={form.currency}
              onValueChange={(v) => updateField("currency", v)}
            >
              <SelectTrigger data-testid="settings-currency" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="CDF">CDF (FC)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Numeric fields */}
          {FIELDS.map(({ key, unit, Icon, color, titleKey, helpKey }) => {
            // Special case: the subscription-fee card carries the "enabled"
            // master switch. When OFF, the amount input is greyed out (still
            // editable, so admins can tune the future amount before
            // re-enabling) — the value is preserved for audit / rollback.
            const isSubscription = key === "subscription_fee_amount";
            const disabled = isSubscription && !form.subscription_fee_enabled;
            return (
              <div
                key={key}
                className={`bg-white border rounded-2xl p-5 transition-opacity ${
                  disabled ? "border-[#F2E2EC] opacity-70" : "border-[#F2E2EC]"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}15`, color }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <Label className="font-display font-black text-[#2A0E3C]">
                          {t(titleKey)}
                        </Label>
                        <p className="text-xs text-[#5C4664] mt-0.5">
                          {isSubscription
                            ? "Montant fixe que l'hôte verse à Milkado pour activer un nouvel événement. Utilisez l'interrupteur pour désactiver la souscription : les commerciaux pourront alors créer des événements sans paiement (le montant reste conservé)."
                            : t(helpKey)}
                        </p>
                      </div>
                      {isSubscription && (
                        <Switch
                          checked={!!form.subscription_fee_enabled}
                          onCheckedChange={(v) => updateField("subscription_fee_enabled", v)}
                          data-testid="settings-subscription-fee-toggle"
                          className="data-[state=checked]:bg-[#D81B8C] shrink-0"
                          aria-label="Activer / désactiver les frais de souscription"
                        />
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Input
                        data-testid={`settings-${key}`}
                        type="number"
                        step={unit === "%" ? "0.1" : "0.01"}
                        min="0"
                        value={form[key] ?? ""}
                        onChange={(e) => updateField(key, e.target.value)}
                        disabled={disabled}
                        className={`rounded-xl max-w-xs ${
                          disabled ? "bg-[#F5F0F3] text-[#9C8CA5] cursor-not-allowed" : ""
                        }`}
                      />
                      <span className={`font-semibold ${disabled ? "text-[#9C8CA5]" : "text-[#5C4664]"}`}>
                        {unit}
                      </span>
                    </div>
                    {isSubscription && (
                      <div
                        data-testid="settings-subscription-fee-status"
                        className={`mt-3 text-xs font-semibold rounded-lg px-3 py-2 ${
                          form.subscription_fee_enabled
                            ? "bg-[#E8F6E5] text-[#2E7A24]"
                            : "bg-[#FFE9B0] text-[#9A6500]"
                        }`}
                      >
                        {form.subscription_fee_enabled
                          ? `ACTIVÉ · les nouveaux événements sont soumis à ${Number(form.subscription_fee_amount || 0).toFixed(0)} $ de souscription.`
                          : "DÉSACTIVÉ · les commerciaux créent les événements sans paiement de souscription."}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Live preview */}
          <div className="bg-[#FFF5FA] border border-[#F2E2EC] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#D81B8C]" />
              <h3 className="font-display font-black text-[#2A0E3C]">
                {t("settings.previewTitle")}
              </h3>
            </div>
            <p className="text-xs text-[#5C4664] mb-3">{t("settings.previewSub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <PreviewBox
                label={t("settings.previewNormal")}
                amount={samplePreview(form.service_fee_normal_percent)}
                color="#D81B8C"
                testid="preview-normal"
              />
              <PreviewBox
                label={t("settings.previewLastMinute")}
                amount={samplePreview(form.service_fee_lastminute_percent)}
                color="#9A6500"
                testid="preview-lastminute"
              />
              <PreviewBox
                label={t("settings.previewSpecial")}
                amount={samplePreview(form.commission_special_percent)}
                color="#6B2C8F"
                testid="preview-special"
              />
            </div>
          </div>

          {/* Cash payment configuration (super_admin) */}
          <div
            data-testid="settings-cash-block"
            className="bg-white border-2 border-[#E8F6E5] rounded-2xl p-5 space-y-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E8F6E5] text-[#3FA535] flex items-center justify-center">
                <Banknote className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display font-black text-[#2A0E3C]">
                  Paiement en espèce — instructions invité
                </h3>
                <p className="text-xs text-[#5C4664]">
                  Affichées à l&apos;invité après validation d&apos;un paiement « Espèce ».
                  Servent aussi de référentiel pour l&apos;équipe Finance.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Fenêtre de paiement (heures)
                </Label>
                <Input
                  data-testid="settings-cash_payment_window_hours"
                  type="number"
                  min="1"
                  max="168"
                  value={form.cash_payment_window_hours ?? 72}
                  onChange={(e) => updateField("cash_payment_window_hours", e.target.value)}
                  className="rounded-xl"
                />
                <p className="text-[11px] text-[#5C4664] mt-1">
                  Au-delà, la contribution passe automatiquement en « expirée » et le cadeau redevient disponible.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-2 border-t border-[#F2E2EC]">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#D81B8C]" />
                <h4 className="font-display font-black text-[#2A0E3C] text-sm">
                  Option A — Au bureau Milkado
                </h4>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
                  Adresse du bureau
                </Label>
                <textarea
                  data-testid="settings-cash_office_address"
                  rows={2}
                  value={form.cash_office_address ?? ""}
                  onChange={(e) => updateField("cash_office_address", e.target.value)}
                  placeholder="Ex: 12, Avenue de la Nation, Gombe, Kinshasa"
                  className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
                  Horaires d&apos;ouverture
                </Label>
                <Input
                  data-testid="settings-cash_office_hours"
                  value={form.cash_office_hours ?? ""}
                  onChange={(e) => updateField("cash_office_hours", e.target.value)}
                  placeholder="Ex: Lun–Ven 9h–17h, Sam 9h–13h"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-2 border-t border-[#F2E2EC]">
              <div className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-[#6B2C8F]" />
                <h4 className="font-display font-black text-[#2A0E3C] text-sm">
                  Option B — Dépôt bancaire
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
                    Nom de la banque
                  </Label>
                  <Input
                    data-testid="settings-cash_bank_name"
                    value={form.cash_bank_name ?? ""}
                    onChange={(e) => updateField("cash_bank_name", e.target.value)}
                    placeholder="Ex: RAWBANK"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
                    Bénéficiaire
                  </Label>
                  <Input
                    data-testid="settings-cash_bank_beneficiary"
                    value={form.cash_bank_beneficiary ?? ""}
                    onChange={(e) => updateField("cash_bank_beneficiary", e.target.value)}
                    placeholder="Ex: MILKADO SARL"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
                  Numéro de compte / IBAN
                </Label>
                <Input
                  data-testid="settings-cash_bank_account"
                  value={form.cash_bank_account ?? ""}
                  onChange={(e) => updateField("cash_bank_account", e.target.value)}
                  placeholder="Ex: 00012-345-678901-23"
                  className="rounded-xl font-mono"
                />
              </div>
            </div>
          </div>

          {error && (
            <p className="text-sm text-[#D81B8C] font-medium" data-testid="settings-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={saving}
            data-testid="settings-save"
            className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-12 px-7 gap-2 font-semibold disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            {saving ? "…" : t("settings.save")}
          </Button>
        </form>
      </div>
    </div>
  );
}

function PreviewBox({ label, amount, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl bg-white border border-[#F2E2EC] p-3"
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">
        {label}
      </div>
      <div className="text-xs text-[#5C4664] mt-0.5">
        $100 cost →
      </div>
      <div
        className="font-display font-black text-xl mt-0.5"
        style={{ color }}
      >
        ${amount}
      </div>
    </div>
  );
}
